"use client";

import {
  serializeSelection,
  type DiscoverSelection,
} from "@/components/discover/filters/selection";
import { getNextOffset } from "@/config/searchConfig";
import { logger } from "@/lib/logger";
import type { TotalRelation } from "@/openSearchClient";
import type { ClubSource, EventSource } from "@/types/discover";
import type { Coords } from "@/types/places";
import type { MapBounds } from "@/utils/mapQueryHelpers";
import {
  hashKey,
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  type QueryKey,
} from "@tanstack/react-query";
import {
  boundsKey,
  buildMapQueryParams,
  fetchMapRoute,
  roundBounds,
} from "./useMapDataQuery";

const PAGE_SIZE = 24;

interface ListItems {
  events: EventSource;
  clubs: ClubSource;
}

export type ListEntity = keyof ListItems;

/** One page of a list. The items sit under the entity name, as in the map routes. */
export type ListPage<E extends ListEntity> = Record<E, ListItems[E][]> & {
  total: number;
  /** "gte": OpenSearch stopped counting at `total`. */
  totalRelation: TotalRelation;
  nextOffset: number | null;
};

interface PageResult<E extends ListEntity> {
  items: ListItems[E][];
  total: number;
  totalRelation?: TotalRelation;
}

/** The map area a list follows instead of the search radius. */
export interface ListViewport {
  bounds: MapBounds;
  selection: DiscoverSelection;
}

/**
 * The routes drop hits without a valid location, so a page can hold fewer
 * items than it asked for. The next page starts after the hits asked for.
 */
function toPage<E extends ListEntity>(
  entity: E,
  { size, from }: { size: number; from: number },
  { items, total, totalRelation = "eq" }: PageResult<E>,
): ListPage<E> {
  return {
    [entity]: items,
    total,
    totalRelation,
    nextOffset: getNextOffset(from + size, total),
  } as ListPage<E>;
}

/**
 * Fetches one list page of the viewport from the map route. `view=list` makes
 * the route use the selection sort; `origin` is the closest-to-me origin.
 */
async function fetchViewportPage<E extends ListEntity>(
  entity: E,
  { bounds, selection }: ListViewport,
  origin: Coords,
  { size, from }: { size: number; from: number },
  signal: AbortSignal,
): Promise<PageResult<E>> {
  const params = buildMapQueryParams(bounds, selection);
  params.set("size", size.toString());
  params.set("from", from.toString());
  params.set("view", "list");
  params.set("originLat", origin.lat.toString());
  params.set("originLng", origin.lon.toString());

  const result = (await fetchMapRoute(entity, params, signal)) as Record<
    E,
    ListItems[E][]
  > & { total: number; totalRelation: TotalRelation };
  return {
    items: result[entity],
    total: result.total,
    totalRelation: result.totalRelation,
  };
}

interface ListQueryOptionsInput<E extends ListEntity> {
  entity: E;
  /** Key of the radius list. It is also the key the server renders. */
  radiusKey: QueryKey;
  fetchRadiusPage: (page: {
    size: number;
    from: number;
  }) => Promise<PageResult<E>>;
  /** Selected location: the origin of the closest-to-me sort. */
  location: Coords;
  /** When set, the list shows this map area instead of the radius. */
  viewport?: ListViewport;
  pageSize?: number;
}

/** Query key and fetcher for one paginated list, by radius or by viewport. */
export function listQueryOptions<E extends ListEntity>({
  entity,
  radiusKey,
  fetchRadiusPage,
  location,
  viewport,
  pageSize = PAGE_SIZE,
}: ListQueryOptionsInput<E>) {
  const common = {
    initialPageParam: 0,
    getNextPageParam: (lastPage: ListPage<E>) => lastPage.nextOffset,
    gcTime: 5 * 60 * 1000,
  };

  if (viewport) {
    // The key and every page use the same rounded bounds, so all pages of a
    // key come from one area.
    const area = { ...viewport, bounds: roundBounds(viewport.bounds) };
    return infiniteQueryOptions<ListPage<E>, Error>({
      ...common,
      queryKey: [
        `paginated-${entity}`,
        "bbox",
        boundsKey(area.bounds),
        serializeSelection(area.selection).toString(),
        `${location.lat},${location.lon}`,
      ],
      queryFn: async ({ pageParam, signal }) => {
        const from = pageParam as number;
        try {
          const page = { size: pageSize, from };
          const result = await fetchViewportPage(
            entity,
            area,
            location,
            page,
            signal,
          );
          return toPage(entity, page, result);
        } catch (error) {
          if (!signal.aborted) {
            logger.error({ msg: `Failed to load viewport ${entity}`, error });
          }
          throw error;
        }
      },
      placeholderData: keepPreviousData,
    });
  }

  return infiniteQueryOptions<ListPage<E>, Error>({
    ...common,
    queryKey: radiusKey,
    queryFn: async ({ pageParam }) => {
      const from = pageParam as number;
      try {
        const page = { size: pageSize, from };
        const result = await fetchRadiusPage(page);
        return toPage(entity, page, result);
      } catch (error) {
        logger.error({ msg: `Failed to load more ${entity}`, error });
        throw error;
      }
    },
  });
}

interface UsePaginatedListQueryInput<E extends ListEntity> {
  entity: E;
  options: ReturnType<typeof listQueryOptions<E>>;
  serverKey: QueryKey;
  initialItems: ListItems[E][];
  initialTotal: number;
  /** Size of the page the server rendered. */
  pageSize?: number;
  enabled: boolean;
}

/**
 * Runs a list query. The SSR page seeds only the key the server rendered; any
 * other key fetches its own page 0.
 */
export function usePaginatedListQuery<E extends ListEntity>({
  entity,
  options,
  serverKey,
  initialItems,
  initialTotal,
  pageSize = PAGE_SIZE,
  enabled,
}: UsePaginatedListQueryInput<E>) {
  const isServerKey = hashKey(serverKey) === hashKey(options.queryKey);

  return useInfiniteQuery({
    ...options,
    initialData: isServerKey
      ? {
          pages: [
            toPage(
              entity,
              { size: Math.max(pageSize, initialItems.length), from: 0 },
              { items: initialItems, total: initialTotal },
            ),
          ],
          pageParams: [0],
        }
      : undefined,
    enabled,
  });
}
